import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useStorageBins, useStorageLocations } from "@/hooks/useStorageBins";
import { StorageBinLabelPreview } from "@/components/labels/StorageBinLabelPreview";
import { useTranslation } from "react-i18next";
import { binLabelFooter } from "@/lib/binLevels";
import { SparePartLabelPrintMenu } from "@/components/labels/SparePartLabelPrintMenu";
import { SettingsBackLink } from "@/components/settings/SettingsBackLink";
import { storageBinToLabelData } from "@/labels/storageBinJobs";
import {
  resolveSparePartLabelSize,
  useLabelPrinter,
  useLabelPrintingSettings,
} from "@/labels";

export default function StorageLabelsPrint() {
  const { t } = useTranslation("inventory");
  const { locationId } = useParams();
  const navigate = useNavigate();
  const { data: bins = [] } = useStorageBins(locationId);
  const { data: locations = [] } = useStorageLocations();
  const location = locations.find((l) => l.id === locationId);

  const { settings: labelPrintingSettings } = useLabelPrintingSettings();
  const { printJobs } = useLabelPrinter();
  // One label size for every print path: Zebra, browser and the preview.
  const size = resolveSparePartLabelSize(labelPrintingSettings.defaultLabelSize);
  const labelData = (b: (typeof bins)[number]) =>
    storageBinToLabelData(b, { locationName: location?.name, footer: binLabelFooter(t, b) });
  const handleZebraPrintAll = async () => {
    await printJobs(
      bins.map((b) => ({
        kind: "storage-bin" as const,
        data: labelData(b),
        options: { size },
      })),
    );
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6 print:hidden">
        <SettingsBackLink to="/settings/storage" label={t("nav.storage", { ns: "settings" })} />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t("adminLabels.binLabels", { location: location?.name ?? "" })}
            </h1>
            <p className="text-muted-foreground">
              {t("adminLabels.labelsReady", { count: bins.length })}
            </p>
          </div>
          <SparePartLabelPrintMenu
            namespace="inventory"
            mode="all"
            variant="default"
            onPrintBrowser={() => window.print()}
            onPrintZebra={handleZebraPrintAll}
            label={t("adminLabels.print")}
            disabled={bins.length === 0}
          />
        </div>
      </div>

      <div className="px-6">
        {/* Print-accurate previews at the real label size; browser printing prints them as shown. */}
        <div className="flex flex-wrap gap-4 print:gap-[4mm]">
          {bins.map((b) => (
            <StorageBinLabelPreview key={b.id} data={labelData(b)} size={size} />
          ))}
        </div>
      </div>

      <style>{`
        @media print {
          @page { margin: 10mm; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
}
